import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import { cn } from "@/lib/utils";

import { BodyText } from "@/components/ui/";
import type { Icon } from "@/components/ui/Icons";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  startAdornment?: Icon;
  endAdornment?: Icon;
  hint?: string;
  requiredErrorHint?: string;
}

/**
 * Controlled input component
 * @param {Icon} startAdornment - JSX element to be placed at the start of the input
 * @param {Icon} endAdornment - JSX element to be placed at the end of the input
 * @param {string} autoComplete - Auto complete attribute for the input. This is ignored by Chrome (doesn't work).
 * @param {string} type - Type of the input
 * @param {string} className - Additional classes to be added
 * @param {boolean} disabled - Disables the input
 * @param {string} placeholder - Placeholder for the input
 * @param {string} hint - Optional text to show under the input
 * @param {boolean} requiredErrorHint - Error text to show when input is required
 * @param {string} value - Value of the input
 * @param {string} name - Name of the input
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      startAdornment,
      endAdornment,
      hint,
      autoComplete = "false",
      type,
      required,
      requiredErrorHint = "This is a required field.",
      value,
      name,
      ...props
    },
    ref,
  ) => {
    const [showRequiredness, setShowRequiredness] = React.useState(false);
    const hasAdornment = Boolean(startAdornment) || Boolean(endAdornment);
    const isError = props["aria-invalid"] === true;
    const handleBlur = React.useCallback(() => {
      if (required && !value) {
        setShowRequiredness(true);
      } else {
        setShowRequiredness(false);
      }
    }, [required, value]);

    const StartAdornment = startAdornment;
    const EndAdornment = endAdornment;

    // This is a workaround to prevent browsers from autofilling an input.
    // Browsers rely on the name attribute for autocompleting something,
    // so a random suffix blocks this.
    const randomSuffix = React.useMemo(() => uuidv4(), []);
    const htmlInputName = `${name}-${randomSuffix}`;

    const inputComponent = hasAdornment ? (
      <div
        className={cn(
          "flex items-center box-border justify-center gap-1 px-2 h-8 rounded-lg border bg-transparent focus-within:border-link data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-tertiary",
          showRequiredness || isError ? "border-error" : "border-secondary",
        )}
        data-disabled={props.disabled}
      >
        {StartAdornment && (
          <StartAdornment
            size={16}
            className={cn(
              "stroke-primary",
              props.disabled && "stroke-disabled",
            )}
          />
        )}
        <input
          type={type}
          autoComplete={autoComplete}
          data-1p-ignore
          className={cn(
            "flex h-full w-full box-border rounded-lg bg-white text-body-sm text-primary placeholder:text-tertiary focus:outline-none disabled:cursor-not-allowed disabled:bg-tertiary disabled:text-tertiary",
            className,
          )}
          onBlur={handleBlur}
          ref={ref}
          value={value}
          name={htmlInputName}
          {...props}
        />
        {EndAdornment && (
          <EndAdornment
            size={16}
            className={cn(
              "stroke-primary",
              props.disabled && "stroke-disabled",
            )}
          />
        )}
      </div>
    ) : (
      <input
        type={type}
        autoComplete={autoComplete}
        className={cn(
          "flex h-8 w-full box-border border rounded-lg bg-white px-3 text-body-sm text-primary ring-offset-white focus:outline-none focus:border-link placeholder:text-tertiary disabled:cursor-not-allowed disabled:bg-tertiary disabled:text-tertiary",
          showRequiredness || isError ? "border-error" : "border-secondary",
          className,
        )}
        onBlur={handleBlur}
        ref={ref}
        value={value}
        name={htmlInputName}
        {...props}
      />
    );

    if (!required && !hint) {
      return inputComponent;
    }

    return (
      <div className="flex flex-col">
        {inputComponent}
        {showRequiredness && (
          <BodyText textColor="error" size="xs">
            {requiredErrorHint}
          </BodyText>
        )}
        {!showRequiredness && hint && (
          <BodyText textColor="tertiary" size="xs">
            {hint}
          </BodyText>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
